/* #/dev/effects comparer (W3-04). Design tokens only (src/ui/styles/tokens.css). */

.page {
  box-sizing: border-box;
  height: 100%;
  overflow: auto;
  padding: var(--space-5) var(--space-6);
  background: var(--color-bg);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--text-md);
  line-height: var(--leading-normal);
}

.page *,
.page *::before,
.page *::after {
  box-sizing: border-box;
}

.header {
  max-width: 980px;
  margin-bottom: var(--space-5);
}

.title {
  margin: 0 0 var(--space-2);
  font-size: var(--text-2xl);
  font-weight: var(--weight-semibold);
}

.intro,
.hint {
  margin: 0;
  color: var(--color-text-muted);
}

.hint {
  font-size: var(--text-sm);
}

.layout {
  display: grid;
  grid-template-columns: minmax(280px, 340px) minmax(0, 1fr);
  gap: var(--space-5);
  align-items: start;
}

@media (max-width: 900px) {
  .layout {
    grid-template-columns: minmax(0, 1fr);
  }
}

.column {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  min-width: 0;
}

.card {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface-1);
}

.cardTitle {
  margin: 0;
  font-size: var(--text-lg);
  font-weight: var(--weight-semibold);
}

.row {
  display: grid;
  grid-template-columns: var(--label-width) minmax(0, 1fr);
  gap: var(--space-3);
  align-items: center;
}

.label {
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

.controls {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  min-width: 0;
}

.select,
.number,
.text,
.textarea {
  min-width: 0;
  height: var(--control-h-md);
  padding: 0 var(--space-3);
  border: var(--border-width) solid var(--color-border-input);
  border-radius: var(--radius-md);
  background: var(--color-surface-sunken);
  color: var(--color-text);
  font: inherit;
}

.select {
  width: 100%;
}

.number {
  width: 84px;
  flex: none;
  font-family: var(--font-mono);
  font-size: var(--text-sm);
}

.text {
  width: 100%;
}

.textarea {
  width: 100%;
  height: auto;
  min-height: 64px;
  padding: var(--space-2) var(--space-3);
  resize: vertical;
}

.range {
  flex: 1;
  min-width: 0;
  accent-color: var(--color-accent);
}

.color {
  width: 44px;
  height: var(--control-h-md);
  padding: 0;
  border: var(--border-width) solid var(--color-border-input);
  border-radius: var(--radius-md);
  background: var(--color-surface-sunken);
}

.select:focus-visible,
.number:focus-visible,
.text:focus-visible,
.textarea:focus-visible,
.range:focus-visible,
.color:focus-visible,
.button:focus-visible,
.segment:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.buttons {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: var(--control-h-md);
  padding: 0 var(--space-4);
  border: var(--border-width) solid var(--color-border-strong);
  border-radius: var(--radius-md);
  background: var(--color-surface-2);
  color: var(--color-text);
  font: inherit;
  font-size: var(--text-sm);
  cursor: pointer;
}

.button:hover:not(:disabled) {
  background: var(--color-surface-hover);
}

.button:disabled {
  color: var(--color-text-disabled);
  cursor: not-allowed;
}

.primary {
  border-color: var(--color-accent-border);
  background: var(--color-accent);
  color: var(--color-text-on-accent);
}

.primary:hover:not(:disabled) {
  background: var(--color-accent-hover);
}

/* A label above its control (for controls too wide to share a row with the label). */
.stack {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
}

/*
 * Segmented buttons. When they do not fit on one line they wrap into full-width rows (every segment grows to fill
 * its row), and the group's background shows through the 1 px gaps as the separators between segments and rows.
 */
.segmented {
  display: flex;
  flex-wrap: wrap;
  gap: var(--border-width);
  width: fit-content;
  max-width: 100%;
  border: var(--border-width) solid var(--color-border-input);
  border-radius: var(--radius-md);
  background: var(--color-border-input);
  overflow: hidden;
}

.stack > .segmented {
  width: 100%;
}

.segment {
  flex: 1 1 auto;
  min-height: var(--control-h-md);
  padding: var(--space-1) var(--space-3);
  border: 0;
  background: var(--color-surface-sunken);
  color: var(--color-text-muted);
  font: inherit;
  font-size: var(--text-sm);
  line-height: var(--leading-tight);
  text-align: center;
  cursor: pointer;
}

.segment:hover:not([aria-pressed='true']) {
  background: var(--color-surface-hover);
}

/* The group clips its corners, so the focus ring of a segment is drawn inside it. */
.segment:focus-visible {
  outline-offset: calc(-1 * var(--focus-ring-width));
}

.segment[aria-pressed='true'] {
  background: var(--color-accent-muted);
  color: var(--color-text);
}

.badge {
  align-self: flex-start;
  padding: var(--space-0) var(--space-3);
  border-radius: var(--radius-pill);
  background: var(--color-warning-muted);
  color: var(--color-warning);
  font-size: var(--text-xs);
  font-weight: var(--weight-medium);
}

.badgeVerified {
  background: var(--color-success-muted);
  color: var(--color-success);
}

.drop {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-3);
  border: var(--border-width) dashed var(--color-border-strong);
  border-radius: var(--radius-md);
  background: var(--color-surface-sunken);
}

.dropActive {
  border-color: var(--color-accent);
  background: var(--color-accent-muted);
}

.dropName {
  color: var(--color-text-muted);
  font-size: var(--text-sm);
  overflow-wrap: anywhere;
}

.panes {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: var(--space-3);
}

.pane {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  margin: 0;
  min-width: 0;
}

.paneTitle {
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

.canvas {
  display: block;
  width: 100%;
  height: auto;
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface-sunken);
  image-rendering: pixelated;
}

.empty {
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 16 / 9;
  padding: var(--space-4);
  border: var(--border-width) dashed var(--color-border);
  border-radius: var(--radius-sm);
  color: var(--color-text-subtle);
  font-size: var(--text-sm);
  text-align: center;
}

.stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: var(--space-3);
  margin: 0;
}

.stat {
  margin: 0;
}

.stat dt {
  color: var(--color-text-muted);
  font-size: var(--text-xs);
}

.stat dd {
  margin: 0;
  font-family: var(--font-mono);
  font-size: var(--text-lg);
}

.notice {
  margin: 0;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  background: var(--color-warning-muted);
  color: var(--color-text);
  font-size: var(--text-sm);
  overflow-wrap: anywhere;
}

.error {
  background: var(--color-danger-muted);
}

.json {
  max-height: 260px;
  margin: 0;
  padding: var(--space-3);
  overflow: auto;
  border-radius: var(--radius-md);
  background: var(--color-surface-sunken);
  color: var(--color-text-muted);
  font-family: var(--font-mono);
  font-size: var(--text-xs);
  white-space: pre;
}

.hidden {
  display: none;
}
